import type { DocumentState } from "@/types/documents";

/**
 * Terminal/complete states - documents that are done processing
 *
 * From database enum `document_state`:
 * - COMPLETE: Fully processed document (building extraction done)
 * - EMBEDDED: Document embeddings generated (terminal for K8s pipeline)
 * - ERROR: Processing failed (terminal)
 * - UNZIPPED: Zip file extracted (terminal for zip files)
 * - IFC_PROCESSED: IFC parsed + glTF generated + relational rows written
 *   (terminal — unlike DWG there's no FINALIZING step because IFC produces no
 *   child documents)
 *
 * Note: CONTAINER and PROCESSED are NOT terminal - they are intermediate states.
 *
 * DWG flow: DWG_CONTAINER → DWG_CONVERTING → DWG_PROCESSED → DWG_FINALIZING → COMPLETE
 * IFC flow: IFC_CONTAINER → IFC_PARSING → IFC_PROCESSED (terminal)
 *
 * All other states (UNPROCESSED, EXTRACTING, EXTRACTED, CLASSIFYING, CLASSIFIED,
 * FOLDERING, FOLDERED, TAGGING, TAGGED, STAGING, STAGED, EXTRACTING_ROOMS,
 * ROOMS_EXTRACTED, EMBEDDING, EXTRACTING_BUILDING, UNZIPPING, CONTAINER,
 * PROCESSED, DWG_CONTAINER, DWG_CONVERTING, DWG_PROCESSED, DWG_FINALIZING,
 * IFC_CONTAINER, IFC_PARSING) are considered "processing" states.
 */
/**
 * The terminal/complete document states — SINGLE SOURCE OF TRUTH (WHA-3689).
 * Importers that need the raw array (e.g. a PostgREST `.not("state","in",…)`
 * filter, or the store's client-side guard) import this instead of re-declaring
 * a copy that can drift. `isDocumentComplete` is the preferred check where a
 * boolean will do.
 */
export const COMPLETE_STATES: readonly string[] = [
  "COMPLETE",
  "EMBEDDED",
  "ERROR",
  "UNZIPPED",
  "IFC_PROCESSED",
];

const COMPLETE_STATES_SET: ReadonlySet<string> = new Set(COMPLETE_STATES);

/**
 * Check if a document is in a complete/terminal state
 * Complete states: Complete, Error, Unzipped
 */
export function isDocumentComplete(state?: string | null): boolean {
  if (!state) return false;
  return COMPLETE_STATES_SET.has(state.toUpperCase());
}

/**
 * Check if a document is currently processing (not in a terminal state)
 */
export function isDocumentProcessing(state?: string | null): boolean {
  if (!state) return true; // No state means pending/unprocessed
  return !COMPLETE_STATES_SET.has(state.toUpperCase());
}

/**
 * States in which a chat attachment (`documents.chatbot_attachment`) cannot be
 * read yet. Such a file is extracted and then PARKS — at EXTRACTED for good,
 * never classified or embedded (franklin-data-pipeline's inline extractor) —
 * so COMPLETE_STATES never applies to it and `isDocumentProcessing` would
 * call it in flight forever. The chat composer gates sends on this same list.
 */
export const CHAT_ATTACHMENT_PENDING_STATES: readonly string[] = [
  "UNPROCESSED",
  "EXTRACTING",
  "CONTAINER",
  "UNZIPPING",
  "DWG_CONTAINER",
  "DWG_CONVERTING",
  // An .ifc never passes through EXTRACTING: it is parsed into the model
  // tables, and the chatbot can read it only at IFC_PROCESSED. Sent earlier,
  // the question is answered with "still processing" and spends a turn.
  // (DWG_PROCESSED / DWG_FINALIZING are deliberately absent: a drawing's
  // converted-PDF child is itself an attachment and may park, leaving the
  // parent there for good. The composer's hold is bounded either way — see
  // QUEUED_SEND_MAX_WAIT_MS in ChatStore.)
  "IFC_CONTAINER",
  "IFC_PARSING",
];

const CHAT_ATTACHMENT_PENDING_SET: ReadonlySet<string> = new Set(CHAT_ATTACHMENT_PENDING_STATES);

/**
 * Whether a document is still being processed, for any document — a chat
 * attachment is done once it is readable, everything else once it is complete.
 */
export function isDocumentStillProcessing(
  state: string | null | undefined,
  chatbotAttachment: boolean | null | undefined
): boolean {
  if (!chatbotAttachment) return isDocumentProcessing(state);
  return !state || CHAT_ATTACHMENT_PENDING_SET.has(state.toUpperCase());
}

/**
 * Format document state for human-readable display
 */
export function formatDocumentState(state?: DocumentState): string {
  if (!state) return "Pending";

  // Normalize state to uppercase to handle any casing issues
  const normalizedState = (state as string).toUpperCase();

  switch (normalizedState) {
    case "UNPROCESSED":
      return "Pending";
    case "CONTAINER":
      return "Zip File";
    // DWG processing states
    case "DWG_CONTAINER":
    case "DWG_CONVERTING":
    case "DWG_PROCESSED":
    case "DWG_FINALIZING":
      return "Document Processing";
    // IFC processing states (IFC_PROCESSED is terminal — handled below)
    case "IFC_CONTAINER":
    case "IFC_PARSING":
      return "Document Processing";
    case "IFC_PROCESSED":
      return "Ready";
    case "EXTRACTING":
      return "Extracting...";
    case "UNZIPPING":
      return "Extracting Zip...";
    case "EXTRACTED":
      return "Extracted";
    case "CLASSIFYING":
      return "Classifying...";
    case "CLASSIFIED":
      return "Classified";
    case "FOLDERING":
      return "Organising...";
    case "FOLDERED":
      return "Organised";
    case "TAGGING":
      return "Tagging...";
    case "TAGGED":
      return "Tagged";
    case "EXTRACTING_ROOMS":
      return "Extracting Rooms...";
    case "ROOMS_EXTRACTED":
      return "Rooms Extracted";
    case "EMBEDDING":
      return "Embedding...";
    case "EMBEDDED":
      return "Embedded";
    case "EXTRACTING_BUILDING":
      return "Extracting Data...";
    case "STAGING":
      return "Staging...";
    case "STAGED":
      return "Staged";
    case "UNZIPPED":
      return "Zip Extracted";
    case "COMPLETE":
      return "Complete";
    case "PROCESSED":
      return "Ready";
    case "ERROR":
      return "Couldn't Process";
    default:
      return "Pending"; // Default to Pending for unknown states
  }
}

/**
 * Get status badge variant based on document state
 */
export function getStateVariant(
  state?: DocumentState
): "default" | "secondary" | "outline" | "destructive" {
  if (!state) return "outline";

  const normalizedState = (state as string).toUpperCase();

  switch (normalizedState) {
    case "UNPROCESSED":
      return "outline";
    case "CONTAINER":
      return "outline";
    // DWG processing states
    case "DWG_CONTAINER":
      return "secondary";
    case "DWG_CONVERTING":
      return "secondary";
    case "DWG_PROCESSED":
      return "secondary";
    case "DWG_FINALIZING":
      return "secondary";
    // IFC processing states
    case "IFC_CONTAINER":
      return "secondary";
    case "IFC_PARSING":
      return "secondary";
    case "IFC_PROCESSED":
      return "default"; // Terminal success state
    case "EXTRACTING":
      return "secondary";
    case "UNZIPPING":
      return "secondary";
    case "EXTRACTED":
      return "secondary";
    case "CLASSIFYING":
      return "secondary";
    case "CLASSIFIED":
      return "secondary";
    case "FOLDERING":
      return "secondary";
    case "FOLDERED":
      return "secondary";
    case "TAGGING":
      return "secondary";
    case "TAGGED":
      return "secondary";
    case "EXTRACTING_ROOMS":
      return "secondary";
    case "ROOMS_EXTRACTED":
      return "secondary";
    case "EMBEDDING":
      return "secondary";
    case "EMBEDDED":
      return "default"; // Terminal success state
    case "EXTRACTING_BUILDING":
      return "secondary";
    case "STAGING":
      return "secondary";
    case "STAGED":
      return "secondary";
    case "UNZIPPED":
      return "default"; // Terminal success state
    case "COMPLETE":
      return "default";
    case "PROCESSED":
      return "default";
    case "ERROR":
      return "destructive";
    default:
      return "outline";
  }
}

/**
 * Get status badge color classes based on document state
 */
export function getStateColorClasses(state?: DocumentState): string {
  if (!state) {
    return "bg-gray-100 text-gray-700 border-gray-200";
  }

  // Normalize state to uppercase to handle any casing issues
  const normalizedState = (state as string).toUpperCase();

  switch (normalizedState) {
    case "UNPROCESSED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    case "CONTAINER":
      return "bg-amber-100 text-amber-700 border-amber-200";
    // DWG processing states
    case "DWG_CONTAINER":
      return "bg-sky-100 text-sky-700 border-sky-200 animate-pulse";
    case "DWG_CONVERTING":
      return "bg-sky-100 text-sky-700 border-sky-200 animate-pulse";
    case "DWG_PROCESSED":
      return "bg-sky-100 text-sky-700 border-sky-200 animate-pulse";
    case "DWG_FINALIZING":
      return "bg-sky-100 text-sky-700 border-sky-200 animate-pulse";
    // IFC processing states
    case "IFC_CONTAINER":
      return "bg-sky-100 text-sky-700 border-sky-200 animate-pulse";
    case "IFC_PARSING":
      return "bg-sky-100 text-sky-700 border-sky-200 animate-pulse";
    case "IFC_PROCESSED":
      return "bg-green-100 text-green-700 border-green-200"; // Terminal success state
    case "EXTRACTING":
      return "bg-blue-100 text-blue-700 border-blue-200 animate-pulse";
    case "UNZIPPING":
      return "bg-amber-100 text-amber-700 border-amber-200 animate-pulse";
    case "EXTRACTED":
      return "bg-cyan-100 text-cyan-700 border-cyan-200";
    case "CLASSIFYING":
      return "bg-purple-100 text-purple-700 border-purple-200 animate-pulse";
    case "CLASSIFIED":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "FOLDERING":
      return "bg-yellow-100 text-yellow-700 border-yellow-200 animate-pulse";
    case "FOLDERED":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "TAGGING":
      return "bg-orange-100 text-orange-700 border-orange-200 animate-pulse";
    case "TAGGED":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "EXTRACTING_ROOMS":
      return "bg-indigo-100 text-indigo-700 border-indigo-200 animate-pulse";
    case "ROOMS_EXTRACTED":
      return "bg-indigo-100 text-indigo-700 border-indigo-200";
    case "EMBEDDING":
      return "bg-pink-100 text-pink-700 border-pink-200 animate-pulse";
    case "EMBEDDED":
      return "bg-green-100 text-green-700 border-green-200"; // Terminal - same as COMPLETE
    case "EXTRACTING_BUILDING":
      return "bg-teal-100 text-teal-700 border-teal-200 animate-pulse";
    case "STAGING":
      return "bg-violet-100 text-violet-700 border-violet-200 animate-pulse";
    case "STAGED":
      return "bg-violet-100 text-violet-700 border-violet-200";
    case "UNZIPPED":
      return "bg-green-100 text-green-700 border-green-200"; // Same as COMPLETE
    case "COMPLETE":
      return "bg-green-100 text-green-700 border-green-200";
    case "PROCESSED":
      return "bg-green-100 text-green-700 border-green-200";
    case "ERROR":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200"; // Default to gray
  }
}
